import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

type Option = { value: string; label: string; color?: string };

/** Keep pointer choices in the text selection; consume menu keys before returning focus. */
export function TextStyleSelect({
  label,
  title,
  value,
  options,
  className,
  decoration,
  onChange,
  onDismiss,
}: {
  label: string;
  title: string;
  value: string;
  options: Option[];
  className?: string;
  decoration?: ReactNode;
  onChange: (value: string) => void;
  onDismiss: () => void;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<"pointer" | "keyboard" | null>(null);
  const [active, setActive] = useState(0);
  const selected = options.findIndex((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    menu.current?.focus();
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !menu.current?.contains(target))
        setOpen(null);
    };
    const close = () => setOpen(null);
    window.addEventListener("pointerdown", outside, true);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("pointerdown", outside, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
    };
  }, [open]);

  const show = (mode: "pointer" | "keyboard") => {
    setActive(Math.max(0, selected));
    setOpen(mode);
  };
  const choose = (next: string) => {
    setOpen(null);
    onChange(next);
  };
  const dismiss = () => {
    setOpen(null);
    trigger.current?.focus();
    onDismiss();
  };
  const rect = open ? trigger.current?.getBoundingClientRect() : null;
  const menuHeight = options.length * 36 + 10;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`text-style-select ${className ?? ""}`}
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={!!open}
        aria-controls={open ? id : undefined}
        title={title}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => (open ? dismiss() : show("pointer"))}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            show("keyboard");
          } else if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            dismiss();
          }
        }}
      >
        {decoration}
        <span>
          {options[selected]?.label ?? (value === "mixed" ? "Mixed" : "Custom")}
        </span>
        <ChevronDown size={12} />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={menu}
            id={id}
            role="listbox"
            aria-label={label}
            aria-activedescendant={`${id}-${active}`}
            tabIndex={-1}
            className="text-style-options floating-surface"
            style={{
              left: Math.max(8, Math.min(window.innerWidth - 188, rect.left)),
              top: Math.max(
                8,
                Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6),
              ),
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (
                [
                  "ArrowDown",
                  "ArrowUp",
                  "Home",
                  "End",
                  "Enter",
                  " ",
                  "Escape",
                ].includes(event.key)
              )
                event.preventDefault();
              if (event.key === "ArrowDown")
                setActive((current) => (current + 1) % options.length);
              else if (event.key === "ArrowUp")
                setActive(
                  (current) => (current + options.length - 1) % options.length,
                );
              else if (event.key === "Home") setActive(0);
              else if (event.key === "End") setActive(options.length - 1);
              else if (event.key === "Enter" || event.key === " ")
                choose(options[active].value);
              else if (event.key === "Escape") dismiss();
              else if (event.key === "Tab") {
                event.preventDefault();
                dismiss();
              }
            }}
          >
            {options.map((option, index) => (
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                id={`${id}-${index}`}
                key={option.value}
                tabIndex={-1}
                className={active === index ? "active" : ""}
                onPointerMove={() => setActive(index)}
                onClick={() => choose(option.value)}
              >
                {option.color && (
                  <span
                    className="text-color-swatch"
                    style={{ background: option.color }}
                  />
                )}
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
